import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';

/**
 * CR-038 공용 엔티티 선택기.
 * 부서·수탁사·담당자(사용자)·위탁 업무처럼 기존 엔티티를 참조하는 입력은 네이티브 select 대신 이것을 쓴다.
 *  - 검색 입력 + 목록 팝오버(키보드 조작) + 새로고침 + 생성 바로가기(새 탭, 권한 있을 때만 caller 가 넘긴다)
 *  - 선택된 값이 목록에 없으면(비활성·종료) fallback 라벨을 그대로 보여주고 목록에도 그 한 건을 유지한다
 *  - 고정 열거값(상태·구분)에는 쓰지 않는다
 */
export interface PickerOption { id: string; label: string; sub?: string; inactive?: boolean; /** 검색 대상 추가 문자열 (코드·이메일·사업자번호) */ keywords?: string; /** 트리 들여쓰기 단계 */ depth?: number; /** 호출자가 쓰는 부가 정보 (예: 위탁 업무의 수탁사·부서) */ meta?: Record<string, string | null> }
export interface EntityPickerProps {
  value: string;
  onChange: (id: string) => void;
  options: PickerOption[];
  loading?: boolean;
  onRefresh?: () => void;
  /** 미선택 표시 문구 (기본 "(없음)") */
  emptyLabel?: string;
  /** 비울 수 있는지 (기본 true) */
  allowEmpty?: boolean;
  /** 생성 바로가기 — 권한이 있을 때만 넘긴다. 새 탭으로 연다 */
  create?: { to: string; label: string } | null;
  /** 선택값이 options 에 없을 때 보여줄 라벨 (예: 비활성 부서 이름) */
  fallbackLabel?: string;
  width?: 'xs' | 'sm' | 'md' | 'lg' | 'full';
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  placeholder?: string;
  id?: string;
  className?: string;
}

function matches(o: PickerOption, q: string) {
  if (!q) return true;
  const hay = `${o.label} ${o.sub ?? ''} ${o.keywords ?? ''}`.toLowerCase();
  return q.toLowerCase().split(/\s+/).filter(Boolean).every((t) => hay.includes(t));
}

export function EntityPicker({ value, onChange, options, loading, onRefresh, emptyLabel = '(없음)', allowEmpty = true, create, fallbackLabel, width = 'md', disabled, readOnly, required, placeholder, id, className = '' }: EntityPickerProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // 팝오버는 폼 격자(overflow: hidden) 에 잘리지 않도록 뷰포트 기준 고정 위치로 띄운다
  const [pos, setPos] = useState<{ top: number; left: number; width: number; up: boolean }>({ top: 0, left: 0, width: 0, up: false });
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => { const r = root.current?.getBoundingClientRect(); if (!r) return; const up = window.innerHeight - r.bottom < 280 && r.top > 280; setPos({ top: up ? r.top : r.bottom + 1, left: r.left, width: r.width, up }); };
    place();
    window.addEventListener('scroll', place, true); window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [open]);
  const selected = options.find((o) => o.id === value);
  const selectedLabel = value ? (selected?.label ?? fallbackLabel ?? '(선택된 항목)') + (selected?.inactive ? ' (비활성)' : '') : '';
  const anyInactive = options.some((o) => o.inactive);
  const list = useMemo(() => {
    const base = options.filter((o) => (showInactive || !o.inactive || o.id === value) && matches(o, q));
    return allowEmpty && !q ? [{ id: '', label: emptyLabel } as PickerOption, ...base] : base;
  }, [options, q, showInactive, allowEmpty, emptyLabel, value]);
  useEffect(() => { setCursor(0); }, [q, open]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (root.current && !root.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  useEffect(() => { const el = listRef.current?.children[cursor] as HTMLElement | undefined; el?.scrollIntoView?.({ block: 'nearest' }); }, [cursor]);
  const pick = (o: PickerOption) => { onChange(o.id); setOpen(false); setQ(''); };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); e.preventDefault(); return; }
    if (!open) return;
    if (e.key === 'ArrowDown') { setCursor((c) => Math.min(c + 1, list.length - 1)); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { setCursor((c) => Math.max(c - 1, 0)); e.preventDefault(); }
    else if (e.key === 'Enter') { if (list[cursor]) pick(list[cursor]); e.preventDefault(); }
    else if (e.key === 'Escape') { setOpen(false); setQ(''); }
  };
  if (readOnly) return <span className="ft-static">{selectedLabel || emptyLabel}</span>;
  return (
    <div ref={root} className={`ep ft-w-${width} ${className}`}>
      <div className={`ep-box ${disabled ? 'ep-disabled' : ''}`}>
        <input id={id} className="ep-input" value={open ? q : selectedLabel} placeholder={open ? '검색…' : placeholder ?? emptyLabel} disabled={disabled} readOnly={!open}
          onFocus={() => { if (!disabled) setOpen(true); }} onClick={() => { if (!disabled) setOpen(true); }} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
          role="combobox" aria-expanded={open} aria-autocomplete="list" autoComplete="off" />
        {/* required 는 숨은 입력으로 폼 검증에 참여 */}
        {required && <input tabIndex={-1} aria-hidden className="ep-req" value={value} onChange={() => {}} required />}
        {value && allowEmpty && !disabled && <button type="button" className="ep-btn" title="지우기" onClick={() => { onChange(''); setQ(''); }} aria-label="지우기"><svg width="10" height="10" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.5" fill="none"><path d="M1 1l8 8M9 1L1 9" /></svg></button>}
        {onRefresh && <button type="button" className={`ep-btn ${loading ? 'ep-spin' : ''}`} title="목록 새로고침" onClick={onRefresh} disabled={disabled || loading} aria-label="목록 새로고침"><svg width="11" height="11" viewBox="0 0 12 12" stroke="currentColor" strokeWidth="1.4" fill="none"><path d="M10.5 6a4.5 4.5 0 1 1-1.3-3.2" /><path d="M10.5 1.5v3h-3" /></svg></button>}
        <button type="button" className="ep-btn" tabIndex={-1} aria-hidden onClick={() => { if (!disabled) setOpen((o) => !o); }}><svg width="10" height="10" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.5" fill="none"><path d="M2 3.5l3 3 3-3" /></svg></button>
      </div>
      {open && (
        <div className="ep-pop" role="listbox" style={{ top: pos.up ? undefined : pos.top, bottom: pos.up ? window.innerHeight - pos.top + 1 : undefined, left: pos.left, minWidth: pos.width }}>
          <ul ref={listRef} className="ep-list">
            {list.map((o, i) => (
              <li key={o.id || '__empty'} className={`ep-item ${i === cursor ? 'ep-active' : ''} ${o.id === value ? 'ep-selected' : ''} ${o.inactive ? 'ep-inactive' : ''}`} style={o.depth ? { paddingLeft: 8 + o.depth * 12 } : undefined}
                onMouseEnter={() => setCursor(i)} onMouseDown={(e) => { e.preventDefault(); pick(o); }}>
                <span className="ep-label">{o.label}{o.inactive && <span className="ep-sub"> (비활성)</span>}</span>{o.sub && <span className="ep-sub">{o.sub}</span>}
              </li>
            ))}
            {list.length === 0 && <li className="ep-empty">{loading ? '불러오는 중…' : '일치하는 항목이 없습니다'}</li>}
          </ul>
          {(create || anyInactive) && (
            <div className="ep-foot">
              {create && <a className="ep-create" href={create.to} target="_blank" rel="noreferrer" onMouseDown={(e) => e.stopPropagation()}>+ {create.label}</a>}
              {anyInactive && <label className="ep-toggle"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />비활성 포함</label>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
